<template>
  <v-container class="main-container d-flex flex-column justify-center">
    <div class="mx-3 my-3">
      <!-- Billing info same as personal info checkbox -->
      <v-row no-gutters>
        <v-col cols="12" align="left">
          <v-btn tile :color="uiColor" v-on:click="fillBillingInfo()" data-test="button-copy-personal-info">{{
            $t("billing_info_copy")
          }}</v-btn>
        </v-col>
      </v-row>
      <!-- Vorname, Nachname -->
      <v-row no-gutters align="center">
        <v-col cols="12" align="left" class="textfield-margin">
          <div class="pb-2" aria-hidden="true">
            <span class="text-label">{{ $t("billing_info.first_name.text") }}</span>
            <span class="text-optional" v-if="tenant !== 'as'">{{ $t("required") }}</span>
            <span class="text-optional" v-if="tenant === 'as'">{{ $t("optional") }}</span>
            <InfoTooltip :text="$t('billing_info.first_name.info')" />
          </div>
          <v-text-field
            v-model="billingInfo.firstname"
            solo
            :class="{ 'input-error': $v.billingInfo.firstname.$error }"
            :aria-label="`${$t('billing_info.first_name.text')} ${tenant === 'as' ? $t('optional') : $t('required')}`"
            :aria-description="$t('billing_info.first_name.info')"
          ></v-text-field>
          <div class="text-error" v-if="$v.billingInfo.firstname.$error">
            {{ $t("required_field") }}
          </div>
        </v-col>
        <v-col cols="12" align="left" class="textfield-margin">
          <div class="pb-2" aria-hidden="true">
            <span class="text-label">{{ $t("billing_info.last_name.text") }}</span>
            <span class="text-optional" v-if="tenant !== 'as'">{{ $t("required") }}</span>
            <span class="text-optional" v-if="tenant === 'as'">{{ $t("optional") }}</span>
            <InfoTooltip :text="$t('billing_info.last_name.info')" />
          </div>
          <v-text-field
            v-model="billingInfo.lastname"
            solo
            :class="{ 'input-error': $v.billingInfo.lastname.$error }"
            :aria-label="`${$t('billing_info.last_name.text')} ${tenant === 'as' ? $t('optional') : $t('required')}`"
            :aria-description="$t('billing_info.last_name.info')"
          ></v-text-field>
          <div class="text-error" v-if="$v.billingInfo.lastname.$error">
            {{ $t("required_field") }}
          </div>
        </v-col>
      </v-row>
      <!-- Titel vor, Titel nach -->
      <v-row no-gutters align="center">
        <v-col cols="6" align="left" class="pr-2 textfield-margin">
          <div class="pb-2" aria-hidden="true">
            <span class="text-label">{{ $t("billing_info.title.text") }}</span>
            <span class="text-optional">{{ $t("optional") }}</span>
            <InfoTooltip :text="$t('billing_info.title.info')" />
          </div>
          <v-text-field
            v-model="billingInfo.title"
            solo
            :aria-label="`${$t('billing_info.title.text')} ${$t('optional')}`"
            :aria-description="$t('billing_info.title.info')"
          ></v-text-field>
        </v-col>
        <v-col cols="6" align="left" class="pl-2 textfield-margin">
          <div class="pb-2" aria-hidden="true">
            <span class="text-label">{{ $t("billing_info.title2.text") }}</span>
            <span class="text-optional">{{ $t("optional") }}</span>
            <InfoTooltip :text="$t('billing_info.title2.info')" />
          </div>
          <v-text-field
            v-model="billingInfo.title2"
            solo
            :aria-label="`${$t('billing_info.title2.text')} ${$t('optional')}`"
            :aria-description="$t('billing_info.title2.info')"
          ></v-text-field>
        </v-col>
      </v-row>
      <!-- Firma, UID-Nr. -->
      <v-row no-gutters align="center">
        <v-col cols="12" md="6" align="left" class="textfield-margin pr-md-2">
          <div class="mb-2" aria-hidden="true">
            <span class="text-label">{{ $t("billing_info.company.text") }}</span>
            <span class="text-optional">{{ $t("optional") }}</span>
            <InfoTooltip :text="$t('billing_info.company.info')" />
          </div>
          <v-text-field
            v-model="billingInfo.company"
            solo
            :aria-label="`${$t('billing_info.company.text')} ${$t('optional')}`"
            :aria-description="$t('billing_info.company.info')"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6" align="left" class="textfield-margin pl-md-2">
          <div class="mb-2" aria-hidden="true">
            <span class="text-label">{{ $t("billing_info.uid.text") }}</span>
            <span class="text-optional">{{ $t("optional") }}</span>
            <InfoTooltip :text="$t('billing_info.uid.info')" />
          </div>
          <v-text-field
            v-model="billingInfo.vatId"
            solo
            :class="{ 'input-error': $v.billingInfo.vatId.$error }"
            :aria-label="`${$t('billing_info.uid.text')} ${$t('optional')}`"
            :aria-description="$t('billing_info.uid.info')"
          ></v-text-field>
          <div class="text-error" v-if="$v.billingInfo.vatId.$error">
            {{ $t("vat_nr") }}
          </div>
        </v-col>
      </v-row>
      <!-- Order Number -->
      <v-row no-gutters align="center" class="mb-2">
        <v-col cols="12" align="left" aria-hidden="true">
          <span class="text-label">{{ $t("billing_info.order_no.text") }}</span>
          <span class="text-optional">{{ $t("optional") }}</span>
          <InfoTooltip :text="$t('billing_info.order_no.info')" />
        </v-col>
      </v-row>
      <v-row no-gutters align="center" class="textfield-margin">
        <v-col cols="12" align="left">
          <v-text-field
            v-model="billingInfo.orderNo"
            solo
            :aria-label="`${$t('billing_info.order_no.text')} ${$t('optional')}`"
            :aria-description="$t('billing_info.order_no.info')"
          ></v-text-field>
        </v-col>
      </v-row>
      <!-- Adresse -->
      <v-row no-gutters align="center" class="mb-2">
        <v-col cols="12" align="left" aria-hidden="true">
          <span class="text-label">{{ $t("billing_info.address.text") }}</span>
          <span class="text-optional">{{ $t("required") }}</span>
          <InfoTooltip :text="$t('billing_info.address.info')" />
        </v-col>
      </v-row>
      <v-row no-gutters align="center" class="textfield-margin">
        <v-col cols="12" align="left">
          <v-text-field
            v-model="billingInfo.address.line1"
            solo
            :class="{ 'input-error': $v.billingInfo.address.line1.$error }"
            :aria-label="`${$t('billing_info.address.text')} ${$t('line_1')} ${$t('required')}`"
            :aria-description="$t('billing_info.address.info')"
          ></v-text-field>
          <div class="text-error" v-if="$v.billingInfo.address.line1.$error && !$v.billingInfo.address.line1.required">
            {{ $t("required_field") }}
          </div>
        </v-col>
      </v-row>
      <v-row no-gutters align="center" class="textfield-margin">
        <v-col cols="12" align="left">
          <v-text-field
            v-model="billingInfo.address.line2"
            solo
            :aria-label="`${$t('billing_info.address.text')} ${$t('line_2')}`"
            :aria-description="$t('billing_info.address.info')"
          ></v-text-field>
        </v-col>
      </v-row>
      <!-- Stadt, PLZ -->
      <v-row no-gutters align="center" class="mb-2">
        <v-col cols="4" align="left" class="pr-2" aria-hidden="true">
          <span class="text-label">{{ $t("billing_info.zip_code.text") }}</span>
          <span class="text-optional">{{ $t("required") }}</span>
          <InfoTooltip :text="$t('billing_info.zip_code.info')" />
        </v-col>
        <v-col cols="8" align="left" class="pl-2" aria-hidden="true">
          <span class="text-label">{{ $t("billing_info.city.text") }}</span>
          <span class="text-optional">{{ $t("required") }}</span>
          <InfoTooltip :text="$t('billing_info.city.info')" />
        </v-col>
      </v-row>
      <v-row no-gutters align="center" class="textfield-margin">
        <v-col cols="4" align="left" class="pr-2">
          <v-text-field
            v-model="billingInfo.address.zip"
            solo
            id="zip"
            :class="{ 'input-error': $v.billingInfo.address.zip.$error }"
            :aria-label="`${$t('billing_info.zip_code.text')} ${$t('required')}`"
            :aria-description="$t('billing_info.zip_code.info')"
          ></v-text-field>
          <div class="text-error" v-if="$v.billingInfo.address.zip.$error && !$v.billingInfo.address.zip.required">
            {{ $t("required_field") }}
          </div>
        </v-col>
        <v-col cols="8" align="left" class="pl-2">
          <v-text-field
            v-model="billingInfo.address.city"
            solo
            :class="{ 'input-error': $v.billingInfo.address.city.$error }"
            :aria-label="`${$t('billing_info.city.text')} ${$t('required')}`"
            :aria-description="$t('billing_info.city.info')"
          ></v-text-field>
          <div class="text-error" v-if="$v.billingInfo.address.city.$error && !$v.billingInfo.address.city.required">
            {{ $t("required_field") }}
          </div>
        </v-col>
      </v-row>
      <!-- State -->
      <v-row no-gutters align="center" v-if="billingInfo.address.country === 'us'" class="mb-2">
        <v-col cols="12" align="left" aria-hidden="true">
          <span class="text-label">{{ $t("personal_info.state.text") }}</span>
          <span class="text-optional">{{ $t("required") }}</span>
          <InfoTooltip :text="$t('personal_info.state.info')" />
        </v-col>
      </v-row>
      <v-row no-gutters align="center" v-if="billingInfo.address.country === 'us'" class="textfield-margin">
        <v-col cols="12" align="left">
          <v-select
            :items="$t('personal_info.state.options')"
            attach
            solo
            :label="$t('personal_info.state.text')"
            class="select"
            :color="uiColor"
            v-model="billingInfo.address.state"
            :item-color="uiColor"
            :class="{
              'input-error': $v.billingInfo.address.state.$error,
              focused: stateFocused,
            }"
            :aria-label="`${$t('billing_info.state.text')} ${$t('required')}`"
            :aria-description="$t('personal_info.state.info')"
          ></v-select>
          <div class="text-error" v-if="$v.billingInfo.address.state.$error && !$v.billingInfo.address.state.required">
            {{ $t("required_field") }}
          </div>
        </v-col>
      </v-row>
      <!-- Land -->
      <v-row no-gutters align="center" class="mb-2">
        <v-col cols="12" align="left" aria-hidden="true">
          <span class="text-label">{{ $t("billing_info.country.text") }}</span>
          <span class="text-optional">{{ $t("required") }}</span>
          <InfoTooltip :text="$t('billing_info.country.info')" />
        </v-col>
      </v-row>
      <v-row no-gutters align="center" class="textfield-margin">
        <v-col cols="12" align="left">
          <v-autocomplete
            :items="getAvalilableCountries"
            item-text="name"
            item-value="code"
            attach
            solo
            :color="uiColor"
            v-model="billingInfo.address.country"
            item-:color="uiColor"
            tabindex="0"
            :class="{
              'input-error': $v.billingInfo.address.country.$error,
              focused: countryFocused,
            }"
            @focus="countryFocused = true"
            @blur="countryFocused = false"
            :aria-label="`${$t('billing_info.country.text')} ${$t('required')}`"
            :aria-description="$t('billing_info.country.info')"
          ></v-autocomplete>
          <div
            class="text-error"
            v-if="$v.billingInfo.address.country.$error && !$v.billingInfo.address.country.required"
          >
            {{ $t("required_field") }}
          </div>
        </v-col>
      </v-row>
      <!-- E-Mail -->
      <v-row no-gutters align="center" class="mb-2">
        <v-col cols="12" align="left" aria-hidden="true">
          <span class="text-label">{{ $t("billing_info.e-mail.text") }}</span>
          <span class="text-optional">{{ $t("required") }}</span>
          <InfoTooltip :text="$t('billing_info.e-mail.info')" />
        </v-col>
      </v-row>
      <v-row no-gutters align="center" class="textfield-margin mb-md-3">
        <v-col cols="12" align="left">
          <v-text-field
            v-model.trim="billingInfo.email"
            solo
            :class="{ 'input-error': $v.billingInfo.email.$error }"
            :aria-label="`${$t('billing_info.e-mail.text')} ${$t('required')}`"
            :aria-description="$t('billing_info.e-mail.info')"
          ></v-text-field>
          <div class="text-error" v-if="$v.billingInfo.email.$error && !$v.billingInfo.email.required">
            {{ $t("required_field") }}
          </div>
          <div class="text-error" v-if="$v.billingInfo.email.$error && !$v.billingInfo.email.email">
            {{ $t("e-mail_invalid") }}
          </div>
        </v-col>
      </v-row>
    </div>
    <!-- Navigation Buttons -->
    <div class="mt-4">
      <v-btn tile :color="uiColor" class="next-button" v-on:click="validateForm" data-test="button-next">{{
        $t("proceed")
      }}</v-btn>
      <v-btn tile :color="uiColor" class="back-button" v-on:click="$emit('back-button-clicked')">{{
        $t("back")
      }}</v-btn>
    </div>
  </v-container>
</template>

<script>
import { required, helpers, email, requiredIf } from "vuelidate/lib/validators";
const vatValidator = helpers.regex("vatValidator", /^[A-Z][A-Z][A-Z0-9-.]{2,13}$/);
import InfoTooltip from "./InfoTooltip";
const emailFormatter = (value) => {
  if (!value) return value;
  let parts = value.split("@");
  if (parts[1]) {
    parts[1] = parts[1].toLowerCase();
  }
  return parts.join("@");
};

export default {
  name: "BillingInfo",
  components: {
    InfoTooltip,
  },
  data() {
    return {
      personalInfo: this.$store.getters["registration/personalInfo"],
      personalInfoAddress: this.$store.getters["registration/personalInfoAddress"],
      countryFocused: false,
      stateFocused: false,
      tenant: this.$store.getters["registration/tenant"],
      uiColor: this.$store.getters["registration/uiColor"],
    };
  },
  watch: {
    billingInfo: {
      deep: true,
      handler(val) {
        if (val.address.country !== "us") {
          this.billingInfo.address.state = null;
        }
      },
    },
  },
  validations: {
    billingInfo: {
      firstname: {
        required: requiredIf(function () {
          if (this.tenant === "as") {
            return false;
          } else {
            return true;
          }
        }),
      },
      lastname: {
        required: requiredIf(function () {
          if (this.tenant === "as") {
            return false;
          } else {
            return true;
          }
        }),
      },
      address: {
        line1: {
          required,
        },
        city: {
          required,
        },
        zip: {
          required,
        },
        country: {
          required,
        },
        state: {
          required: requiredIf(function () {
            return this.billingInfo.address.country === "us";
          }),
        },
      },
      vatId: {
        vatValidator,
      },
      email: {
        required,
        email: (val) => email(emailFormatter(val)),
      },
    },
  },
  methods: {
    validateForm() {
      this.$v.$touch();
      if (!this.$v.$error) {
        this.$emit("next-button-clicked");
      }
    },
    fillBillingInfo() {
      this.billingInfo.title = JSON.parse(JSON.stringify(this.personalInfo.title));
      this.billingInfo.title2 = JSON.parse(JSON.stringify(this.personalInfo.title2));
      this.billingInfo.firstname = JSON.parse(JSON.stringify(this.personalInfo.firstname));
      this.billingInfo.lastname = JSON.parse(JSON.stringify(this.personalInfo.lastname));
      if (this.tenant !== "as") {
        this.billingInfo.company = JSON.parse(JSON.stringify(this.personalInfo.company));
      }
      this.billingInfo.address.line1 = JSON.parse(JSON.stringify(this.personalInfoAddress.line1));
      this.billingInfo.address.line2 = JSON.parse(JSON.stringify(this.personalInfoAddress.line2));
      this.billingInfo.address.city = JSON.parse(JSON.stringify(this.personalInfoAddress.city));
      this.billingInfo.address.zip = JSON.parse(JSON.stringify(this.personalInfoAddress.zip));
      this.billingInfo.address.country = JSON.parse(JSON.stringify(this.personalInfoAddress.country));
      this.billingInfo.address.state = JSON.parse(JSON.stringify(this.personalInfoAddress.state));
      this.billingInfo.email = JSON.parse(JSON.stringify(this.personalInfo.email));
    },
  },
  computed: {
    billingInfo: {
      get() {
        return this.$store.getters["registration/billingInfo"];
      },
      set(value) {
        this.$store.dispatch("registration/updateBillingInfo", value);
      },
    },
    getAvalilableCountries() {
      return this.$store.getters["registration/availableCountriesForBilling"];
    },
  },
};
</script>

<style scoped>
.main-container {
  height: 100%;
}
.textfield-margin {
  margin-bottom: -14px;
}
.v-text-field.v-text-field--enclosed .v-text-field__details {
  height: 0;
  padding: 0;
  margin: 0;
}
.black--text ::v-deep label {
  font-size: 18px;
  color: rgba(0, 0, 0, 0.7);
}
.v-input--radio-group.v-input--radio-group--row .v-radio {
  margin-right: 8px;
}

.input-error ::v-deep .v-input__slot {
  box-shadow: 0px 3px 1px -2px rgba(240, 64, 79, 0.2), 0px 2px 2px 0px rgba(240, 64, 79, 0.2),
    0px 1px 5px 0px rgba(240, 64, 79, 0.2) !important;
}
.focused ::v-deep .v-input__slot {
  box-shadow: 0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 6px 10px 0px rgba(0, 0, 0, 0.14),
    0px 1px 18px 0px rgba(0, 0, 0, 0.12) !important;
}
</style>
